import { Component, ElementRef, inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { UploadProgressService } from '../../services/upload-progress.service';
import { UploadProgressItem } from '../../models/upload-progress-item';
import { toSignal } from '@angular/core/rxjs-interop';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';

@Component({
  selector: 'app-upload-progress-panel',
  standalone: true,
  imports: [CommonModule, NgbTooltipModule],
  templateUrl: './upload-progress-panel.component.html',
  styleUrl: './upload-progress-panel.component.scss'
})
export class UploadProgressPanelComponent {
  @ViewChild('resumeFileInput') resumeFileInput!: ElementRef<HTMLInputElement>;

  protected readonly uploadProgress = inject(UploadProgressService);
  protected readonly expanded = toSignal(this.uploadProgress.panelExpanded$, { initialValue: false });
  items: UploadProgressItem[] = [];

  /** Id del ítem que se está reanudando (para asociar el archivo seleccionado). */
  resumeItemId: string | null = null;
  /** Mensaje de error al reanudar por ítem (archivo no coincide). */
  resumeErrorByItemId: Record<string, string> = {};

  constructor() {
    this.uploadProgress.items$.subscribe(list => (this.items = list));
  }

  get reversedItems(): UploadProgressItem[] {
    return [...this.items].reverse();
  }

  isInterrupted(item: UploadProgressItem): boolean {
    return this.uploadProgress.canResume(item);
  }

  /** True si el ítem se está subiendo ahora mismo (tiene File real y no ha terminado). */
  isUploading(item: UploadProgressItem): boolean {
    return !item.completed && !item.error && item.file instanceof File;
  }

  /** Abre el selector de archivo para reanudar la subida del ítem. */
  triggerResumeFileSelect(item: UploadProgressItem): void {
    delete this.resumeErrorByItemId[item.id];
    this.resumeItemId = item.id;
    this.resumeFileInput?.nativeElement?.click();
  }

  onResumeFileSelected(event: Event): void {
    console.log("onResumeFileSelected", event);
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    const itemId = this.resumeItemId;
    input.value = '';
    this.resumeItemId = null;
    if (!file || !itemId) return;
    const result = this.uploadProgress.resumeUpload(itemId, file);
    if (result.success) {
      delete this.resumeErrorByItemId[itemId];
    } else {
      this.resumeErrorByItemId[itemId] = result.error ?? 'No se pudo reanudar la subida.';
    }
  }

  getResumeError(itemId: string): string | null {
    return this.resumeErrorByItemId[itemId] ?? null;
  }

  removeCompleted(): void {
    this.uploadProgress.removeCompleted();
  }

  removeItemFromList(item: UploadProgressItem): void {
    this.uploadProgress.removeItem(item.id);
  }

  /** Cancela y elimina un ítem que está pendiente de reanudar. */
  cancelItem(item: UploadProgressItem): void {
    delete this.resumeErrorByItemId[item.id];
    this.uploadProgress.removeItem(item.id);
  }

  hasCompletedItems(): boolean {
    return this.items.some(it => it.completed);
  }

  hasUploadsInProgress(): boolean {
    return this.items.some(it => !it.completed);
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  }

  getItemTooltip(item: UploadProgressItem): string {
    const parts = [
      item.file?.name ?? 'Archivo',
      item.file?.size != null ? this.formatFileSize(item.file.size) : '',
      `${item.progress}%`,
      item.documentTitle ? `Documento: ${item.documentTitle}` : '',
      item.chatName ? `Chat: ${item.chatName}` : '',
      item.projectName ? `Proyecto: ${item.projectName}` : '',
      item.clientName ? `Cliente: ${item.clientName}` : ''
    ].filter(Boolean);
    if (item.error) parts.unshift('Error en la subida');
    if (item.completed && !item.error) parts.push('Completado');
    if (this.isInterrupted(item)) parts.push('Subida interrumpida');
    return parts.join('\n');
  }

  getCircleProgressBg(item: UploadProgressItem): string {
    if (item.error) return 'conic-gradient(#dc3545 0% 100%, #dc3545 100% 100%)';
    if (item.completed) return 'conic-gradient(#198754 0% 100%, #198754 100% 100%)';
    if (this.isInterrupted(item)) {
      const p = item.progress;
      return `conic-gradient(#fd7e14 0% ${p}%, #e9ecef ${p}% 100%)`;
    }
    const p = item.progress;
    return `conic-gradient(#0d6efd 0% ${p}%, #e9ecef ${p}% 100%)`;
  }
}
